import React, { useState, useEffect, useCallback } from 'react';
import { TTS_MODELS, SUPPORTED_LANGUAGES, LLM_MODELS, SAMPLE_VOICES, Voice } from './AgentWizard/types';
import { useVoicePreview } from './AgentWizard/utils/voicePreview';
import { createPortal } from 'react-dom';
import AgentService from '../../services/agentService';

interface Agent {
  agent_id: string;
  name: string;
  created_at_unix_secs?: number;
  access_info?: {
    is_creator: boolean;
    creator_name: string;
    creator_email: string;
    role: string;
  };
  conversation_config?: any;
}

interface AgentFormProps {
  apiKey: string;
  agent?: Agent | null;
  onClose: () => void;
}

const AgentForm: React.FC<AgentFormProps> = ({ apiKey, agent, onClose }) => {
  // Build initial form state based on existing agent configuration (if editing)
  const buildInitialFormData = () => {
    if (!agent || !agent.conversation_config) {
      return {
        name: agent?.name || '',
        firstMessage: 'Hello, how can I help you today?',
        prompt: 'You are a helpful assistant that can answer questions about the topic of the conversation.',
        language: 'en',
        voiceId: 'cjVigY5qzO86Huf0OWal',
        modelId: 'eleven_turbo_v2',
        maxDuration: 600,
        turnTimeout: 7,
        temperature: 0,
        llm: 'gemini-2.0-flash-001',
      };
    }

    const cfg: any = agent.conversation_config;
    return {
      name: agent.name || '',
      firstMessage: cfg?.agent?.first_message ?? '',
      prompt: cfg?.agent?.prompt?.prompt ?? '',
      language: cfg?.agent?.language ?? 'en',
      voiceId: cfg?.tts?.voice_id ?? '',
      modelId: cfg?.tts?.model_id ?? '',
      maxDuration: cfg?.conversation?.max_duration_seconds ?? 600,
      turnTimeout: cfg?.turn?.turn_timeout ?? 7,
      temperature: cfg?.agent?.prompt?.temperature ?? 0,
      llm: cfg?.agent?.prompt?.llm ?? 'gemini-2.0-flash-001',
      // Preserve tool fields for later use
      toolIds: cfg?.agent?.prompt?.tool_ids ?? [],
      builtInTools: cfg?.agent?.prompt?.built_in_tools ?? {}
    };
  };

  const [formData, setFormData] = useState(buildInitialFormData());

  // Update form data if the agent prop changes (e.g., switching between edit/new)
  useEffect(() => {
    setFormData(buildInitialFormData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Voice selection state
  const [voices, setVoices] = useState<Voice[]>(SAMPLE_VOICES);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [voiceSearchQuery, setVoiceSearchQuery] = useState('');

  const { playPreview } = useVoicePreview(apiKey);

  const fetchVoices = useCallback(async () => {
    setVoiceLoading(true);
    setVoiceError(null);
    try {
      const response = await fetch('https://api.elevenlabs.io/v1/voices', {
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch voices: ${response.status}`);
      }
      const result = await response.json();
      setVoices(result.voices || SAMPLE_VOICES);
    } catch (err) {
      console.error('Error fetching voices:', err);
      setVoiceError('Unable to load voices from ElevenLabs. Using sample voices.');
      setVoices(SAMPLE_VOICES);
    } finally {
      setVoiceLoading(false);
    }
  }, [apiKey]);

  useEffect(() => {
    fetchVoices();
  }, [fetchVoices]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

    const filteredVoices = voices.filter(v => {
    if (!voiceSearchQuery) return true;
    const q = voiceSearchQuery.toLowerCase();
    return (
      v.name?.toLowerCase().includes(q) ||
      v.description?.toLowerCase().includes(q) ||
      v.category?.toLowerCase().includes(q)
    );
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const agentService = new AgentService(apiKey);
      
      const requestBody = {
        name: formData.name,
        conversation_config: {
          agent: {
            first_message: formData.firstMessage,
            language: formData.language,
            prompt: {
              prompt: formData.prompt,
              llm: formData.llm,
              temperature: Number(formData.temperature),
              max_tokens: -1,
              tool_ids: formData.toolIds ?? [],
              built_in_tools: formData.builtInTools ?? {}
            }
          },
          tts: {
            model_id: formData.modelId,
            voice_id: formData.voiceId,
            agent_output_audio_format: "pcm_16000",
            optimize_streaming_latency: 3,
            stability: 0.5,
            speed: 1,
            similarity_boost: 0.8,
          },
          asr: {
            quality: "high",
            provider: "elevenlabs",
            user_input_audio_format: "pcm_16000",
          },
          turn: {
            turn_timeout: Number(formData.turnTimeout),
            silence_end_call_timeout: -1,
            mode: "turn",
          },
          conversation: {
            max_duration_seconds: Number(formData.maxDuration),
            client_events: ["audio", "interruption"],
          }
        }
      };

      let result;
      if (agent) {
        result = await agentService.updateAgent(agent.agent_id, requestBody);
      } else {
        result = await agentService.createAgent(requestBody);
      }

      if (!result) {
        throw new Error(`Failed to ${agent ? 'update' : 'create'} agent`);
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${agent ? 'update' : 'create'} agent`);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 z-50 overflow-y-auto">
      <div className="max-w-4xl mx-auto py-10 px-4">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">
                <span className="gradient-text">
                  {agent ? 'Edit Agent' : 'Create New Agent'}
                </span>
              </h2>
              <p className="text-gray-400">
                {agent ? 'Update your AI agent configuration' : 'Configure your intelligent conversational agent'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl transition-colors hover:bg-white/10 rounded-lg p-2"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="glass-card rounded-xl p-4 mb-6 border border-red-500/30 neon-border">
              <div className="flex items-center space-x-3">
                <span className="text-red-400 text-xl">❌</span>
                <p className="text-red-300">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div className="glass-effect rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <span className="text-2xl mr-3">ℹ️</span>
                Basic Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Agent Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter agent name"
                  />
                </div>

                <div>
                  <label htmlFor="firstMessage" className="block text-sm font-medium text-gray-300 mb-2">
                    First Message
                  </label>
                  <textarea
                    id="firstMessage"
                    name="firstMessage"
                    value={formData.firstMessage}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Initial greeting message"
                  />
                </div>

                <div>
                  <label htmlFor="language" className="block text-sm font-medium text-gray-300 mb-2">
                    Language
                  </label>
                  <select
                    id="language"
                    name="language"
                    value={formData.language}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {SUPPORTED_LANGUAGES.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* AI Configuration */}
            <div className="glass-effect rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <span className="text-2xl mr-3">🧠</span>
                AI Configuration
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="prompt" className="block text-sm font-medium text-gray-300 mb-2">
                    System Prompt
                  </label>
                  <textarea
                    id="prompt"
                    name="prompt"
                    value={formData.prompt}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Define the agent's behavior and personality"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="llm" className="block text-sm font-medium text-gray-300 mb-2">
                      LLM Model
                    </label>
                    <select
                      id="llm"
                      name="llm"
                      value={formData.llm}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {Object.entries(LLM_MODELS).flatMap(([provider, models]) => 
                        (models as any[]).map(model => (
                          <option key={model.id} value={model.id}>
                            {model.icon} {model.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="temperature" className="block text-sm font-medium text-gray-300 mb-2">
                      Temperature (0-1)
                    </label>
                    <input
                      type="number"
                      id="temperature"
                      name="temperature"
                      value={formData.temperature}
                      onChange={handleInputChange}
                      min="0"
                      max="1"
                      step="0.1"
                      className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Voice Configuration */}
            <div className="glass-effect rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <span className="text-2xl mr-3">🎤</span>
                Voice Configuration
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4 col-span-full">
                  <label className="block text-sm font-medium text-gray-300">Voice</label>
                  <input
                    type="text"
                    placeholder="Search voices..."
                    value={voiceSearchQuery}
                    onChange={(e)=>setVoiceSearchQuery(e.target.value)}
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {voiceLoading && <p className="text-gray-400 text-sm">Loading voices...</p>}
                  {voiceError && <p className="text-yellow-400 text-sm">{voiceError}</p>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                    {filteredVoices.map(voice => (
                      <div
                        key={voice.voice_id}
                        onClick={() => setFormData(prev => ({ ...prev, voiceId: voice.voice_id }))}
                        className={`bg-gray-800 rounded-lg p-3 border cursor-pointer transition-all ${formData.voiceId === voice.voice_id ? 'border-blue-500 bg-blue-600/20' : 'border-gray-600 hover:border-blue-400 hover:bg-gray-700'}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white truncate">{voice.name}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); playPreview(voice.voice_id); }}
                            className="text-blue-400 hover:text-blue-300 text-sm"
                          >
                            ▶️
                          </button>
                        </div>
                        {voice.description && (
                          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{voice.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="modelId" className="block text-sm font-medium text-gray-300 mb-2">
                    TTS Model
                  </label>
                  <select
                    id="modelId"
                    name="modelId"
                    value={formData.modelId}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {TTS_MODELS.map(model => (
                        <option key={model.id} value={model.id}>
                          {model.icon} {model.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Conversation Settings */}
            <div className="glass-effect rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <span className="text-2xl mr-3">⚙️</span>
                Conversation Settings
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="maxDuration" className="block text-sm font-medium text-gray-300 mb-2">
                    Max Duration (seconds)
                  </label>
                  <input
                    type="number"
                    id="maxDuration"
                    name="maxDuration"
                    value={formData.maxDuration}
                    onChange={handleInputChange}
                    min="60"
                    max="3600"
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="turnTimeout" className="block text-sm font-medium text-gray-300 mb-2">
                    Turn Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    id="turnTimeout"
                    name="turnTimeout"
                    value={formData.turnTimeout}
                    onChange={handleInputChange}
                    min="1"
                    max="30"
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-white/10">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary px-6 py-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary px-6 py-3 disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  agent ? '✏️ Update Agent' : '🚀 Create Agent'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AgentForm; 